import React from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  Grid,
  GridItem,
  Switch,
  FormHelperText,
  Text,
  Box,
  Divider,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  IconButton,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';

/**
 * فرم مدیریت ساعات کاری مشاور
 * @param {Object} props - props کامپوننت
 * @param {Object} props.workingHours - ساعات کاری فعلی مشاور
 * @param {number} props.sessionDuration - طول هر جلسه (دقیقه)
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const WorkingHoursForm = React.forwardRef(({ workingHours, sessionDuration, onSubmit, isLoading }, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      workingHours: (workingHours || [
        { day: 'saturday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'sunday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'monday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'tuesday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'wednesday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'thursday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] },
        { day: 'friday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', breaks: [] }
      ]).map(day => ({
        ...day,
        breaks: Array.isArray(day.breaks) ? day.breaks : []
      })),
      sessionDuration: sessionDuration || 60,
    },
  });

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  const dayNames = {
    saturday: 'شنبه',
    sunday: 'یکشنبه',
    monday: 'دوشنبه',
    tuesday: 'سه‌شنبه',
    wednesday: 'چهارشنبه',
    thursday: 'پنج‌شنبه',
    friday: 'جمعه'
  };

  const handleWorkingDayToggle = (index, isWorking) => {
    setValue(`workingHours.${index}.isWorking`, isWorking);
  };

  const addBreak = (dayIndex) => {
    const currentBreaks = watch(`workingHours.${dayIndex}.breaks`) || [];
    setValue(`workingHours.${dayIndex}.breaks`, [
      ...currentBreaks,
      { startTime: '12:00', endTime: '13:00' }
    ]);
  };

  const removeBreak = (dayIndex, breakIndex) => {
    const currentBreaks = watch(`workingHours.${dayIndex}.breaks`) || [];
    const newBreaks = currentBreaks.filter((_, i) => i !== breakIndex);
    setValue(`workingHours.${dayIndex}.breaks`, newBreaks);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={6} align="stretch">
        {/* طول هر جلسه */}
        <FormControl isInvalid={errors.sessionDuration}>
          <FormLabel>طول هر جلسه (دقیقه) *</FormLabel>
          <NumberInput
            min={15}
            max={180}
            defaultValue={sessionDuration || 60}
            onChange={(value) => setValue('sessionDuration', parseInt(value))}
          >
            <NumberInputField
              {...register('sessionDuration', {
                required: 'طول جلسه الزامی است',
                min: { value: 15, message: 'حداقل طول جلسه 15 دقیقه است' },
                max: { value: 180, message: 'حداکثر طول جلسه 180 دقیقه است' },
              })}
              placeholder="60"
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>طول هر جلسه مشاوره به دقیقه (15 تا 180 دقیقه)</FormHelperText>
          <FormErrorMessage>
            {errors.sessionDuration && errors.sessionDuration.message}
          </FormErrorMessage>
        </FormControl>

        <Divider />

        {/* ساعات کاری روزهای هفته */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            ساعات کاری روزهای هفته
          </Text>
          
          {watch('workingHours').map((day, index) => (
            <Box key={day.day} mb={4} p={4} border="1px" borderColor="gray.200" borderRadius="md">
              <VStack spacing={4} align="stretch">
                {/* ردیف اول: نام روز و وضعیت */}
                <HStack justify="space-between" align="center">
                  <Text fontWeight="medium" fontSize="md">
                    {dayNames[day.day]}
                  </Text>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <Switch
                      isChecked={watch(`workingHours.${index}.isWorking`)}
                      onChange={(e) => handleWorkingDayToggle(index, e.target.checked)}
                      colorScheme="blue"
                    />
                    <FormLabel ml={2} mb="0" fontSize="sm">
                      {watch(`workingHours.${index}.isWorking`) ? 'فعال' : 'غیرفعال'}
                    </FormLabel>
                  </FormControl>
                </HStack>

                {/* ردیف دوم: ساعات کاری (فقط اگر فعال باشد) */}
                {watch(`workingHours.${index}.isWorking`) && (
                  <>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      {/* ساعت شروع */}
                      <GridItem>
                        <FormControl isInvalid={errors[`workingHours.${index}.startTime`]}>
                          <FormLabel fontSize="sm">ساعت شروع</FormLabel>
                          <Input
                            type="time"
                            {...register(`workingHours.${index}.startTime`, {
                              required: 'ساعت شروع الزامی است',
                            })}
                          />
                          <FormErrorMessage>
                            {errors[`workingHours.${index}.startTime`] && errors[`workingHours.${index}.startTime`].message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      {/* ساعت پایان */}
                      <GridItem>
                        <FormControl isInvalid={errors[`workingHours.${index}.endTime`]}>
                          <FormLabel fontSize="sm">ساعت پایان</FormLabel>
                          <Input
                            type="time"
                            {...register(`workingHours.${index}.endTime`, {
                              required: 'ساعت پایان الزامی است',
                            })}
                          />
                          <FormErrorMessage>
                            {errors[`workingHours.${index}.endTime`] && errors[`workingHours.${index}.endTime`].message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      {/* ساعت استراحت (قدیمی - برای سازگاری) */}
                      <GridItem>
                        <FormControl isInvalid={errors[`workingHours.${index}.breakStart`]}>
                          <FormLabel fontSize="sm">زمان استراحت (قدیمی)</FormLabel>
                          <HStack spacing={2}>
                            <Input
                              type="time"
                              size="sm"
                              {...register(`workingHours.${index}.breakStart`)}
                              placeholder="12:00"
                              flex="1"
                            />
                            <Text fontSize="xs" minW="20px" textAlign="center">تا</Text>
                            <Input
                              type="time"
                              size="sm"
                              {...register(`workingHours.${index}.breakEnd`)}
                              placeholder="13:00"
                              flex="1"
                            />
                          </HStack>
                          <FormHelperText fontSize="xs" color="gray.500">
                            برای سازگاری با سیستم قدیمی (اختیاری)
                          </FormHelperText>
                          <FormErrorMessage>
                            {errors[`workingHours.${index}.breakStart`] && errors[`workingHours.${index}.breakStart`].message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    {/* استراحت‌های دائمی (چند بازه) */}
                    <Box mt={4} p={3} bg="gray.50" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <FormLabel fontSize="sm" mb={0}>استراحت‌های دائمی</FormLabel>
                      <Button
                        size="xs"
                        leftIcon={<FaPlus />}
                        colorScheme="teal"
                        onClick={() => addBreak(index)}
                      >
                        افزودن استراحت
                      </Button>
                    </HStack>
                    <FormHelperText fontSize="xs" color="gray.500" mb={2}>
                      می‌توانید چند بازه استراحت برای این روز تعریف کنید (مثلاً ناهار و عصرانه)
                    </FormHelperText>
                    <VStack spacing={2} align="stretch">
                      {(watch(`workingHours.${index}.breaks`) || []).map((br, brIndex) => (
                        <HStack key={brIndex} spacing={2}>
                          <Input
                            type="time"
                            size="sm"
                            {...register(`workingHours.${index}.breaks.${brIndex}.startTime`, {
                              required: 'زمان شروع الزامی است'
                            })}
                            placeholder="12:00"
                            flex="1"
                          />
                          <Text fontSize="xs" minW="20px" textAlign="center">تا</Text>
                          <Input
                            type="time"
                            size="sm"
                            {...register(`workingHours.${index}.breaks.${brIndex}.endTime`, {
                              required: 'زمان پایان الزامی است'
                            })}
                            placeholder="13:00"
                            flex="1"
                          />
                          <IconButton
                            icon={<FaTrash />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeBreak(index, brIndex)}
                            aria-label="حذف استراحت"
                          />
                        </HStack>
                      ))}
                      {(!watch(`workingHours.${index}.breaks`) || watch(`workingHours.${index}.breaks`).length === 0) && (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>
                          هنوز استراحتی اضافه نشده است
                        </Text>
                      )}
                    </VStack>
                  </Box>
                  </>
                )}
              </VStack>
            </Box>
          ))}
        </Box>
      </VStack>
    </form>
  );
});

export default WorkingHoursForm;
